<script>
  import taxYear from "$data/2025-26.json";
  import {
    lisaBonusFor,
    canOpenLISA,
  } from "$calc/allowances.js";
  import { netCostOfContribution, marginalRates } from "$calc/pension.js";
  import { annualInterestRate, annualRepayment } from "$calc/plans.js";
  import { graduate } from "./appState.svelte.js";

  const LIFE_STAGES = [
    { key: "child", label: "Under 18 (child)" },
    { key: "preuni", label: "Pre-university (18+)" },
    { key: "student", label: "At university" },
    { key: "grad", label: "Recent graduate / working" },
  ];

  const HOUSING_INTENTS = [
    { key: "yes", label: "Yes, saving toward one" },
    { key: "maybe", label: "Maybe eventually" },
    { key: "no", label: "No, or already own" },
  ];

  const EMERGENCY_STATES = [
    { key: "none", label: "None (haven't started)" },
    { key: "some", label: "Less than 3 months' expenses" },
    { key: "ok", label: "3–6 months' expenses" },
    { key: "full", label: "6+ months' expenses" },
  ];

  // Inputs
  let giftAmount = $state(5000);
  let lifeStage = $state("grad");
  // age now shared via graduate.currentAge
  let firstHome = $state("maybe");
  let emergencyState = $state("some");
  let hasPensionMatch = $state(true);
  let matchPctRemaining = $state(3); // % of salary still unmatched
  let realReturn = $state(4.5);
  let horizonYears = $state(20); // for long-term projection

  // Derived
  const band = $derived(taxYear.incomeTax.englandNIWales);
  const niData = $derived(taxYear.nationalInsurance.class1Employee);
  const marginal = $derived(marginalRates(graduate.salary, band, niData));
  const planData = $derived(taxYear.studentLoans[graduate.planKey]);
  const rpiMarch = $derived(taxYear.assumedIndices.rpiMarchDefault);

  const isChild = $derived(lifeStage === "child");
  const lisaEligible = $derived(!isChild && canOpenLISA(graduate.currentAge, taxYear.isaLisa));

  // Compute each option's "£1 in this now → £X at horizon", plus trade-offs
  const options = $derived.by(() => {
    const gift = Math.max(0, giftAmount);
    const yearsGrow = Math.max(0, horizonYears);
    const growthFactor = Math.pow(1 + realReturn / 100, yearsGrow);
    const opts = [];

    // --- Emergency fund ---
    if (emergencyState === "none" || emergencyState === "some") {
      opts.push({
        key: "emergency",
        label: "Top up emergency fund",
        badge: emergencyState === "none" ? "start here" : "still under 3 months",
        badgeTone: "warning",
        immediateValue: gift,
        immediateMultiple: 1.0,
        horizonValue: gift * Math.pow(1.04, yearsGrow),  // easy-access ~4% but really this is spent when needed
        access: "Immediate",
        constraints: "Held in easy-access; interest taxed unless in Cash ISA.",
        why: emergencyState === "none"
          ? "Before optimising, get 3–6 months' essential outgoings into an easy-access account. Doesn't need to be all of the gift — but nothing else should happen until this is at least started."
          : "You're under 3 months. Fill the buffer first; investing while under-buffered means selling at a bad price if life happens.",
        weight: 100,
      });
    }

    // --- Employer match top-up (via pension) ---
    if (!isChild && hasPensionMatch && matchPctRemaining > 0) {
      const salary = Math.max(1, graduate.salary);
      const roomToMatch = salary * (matchPctRemaining / 100);
      const contributionUsable = Math.min(gift, roomToMatch);
      // Salary-sacrifice route assumed for the match top-up:
      const netCost = netCostOfContribution(
        contributionUsable, marginal.tax, marginal.ni, "salarySacrifice",
      );
      // Employer 1:1 up to cap assumed; user should confirm from Pension tab.
      const totalInPension = contributionUsable * 2; // employee + 1:1 match
      opts.push({
        key: "match",
        label: "Fill unclaimed employer pension match",
        badge: "instant 100%+",
        badgeTone: "success",
        immediateValue: totalInPension,
        immediateMultiple: netCost > 0 ? totalInPension / netCost : Infinity,
        horizonValue: totalInPension * growthFactor,
        access: "Locked until age 55–58",
        constraints: `Uses £${contributionUsable.toFixed(0)} of the gift (the room left before match cap). Assumes 1:1 match — check your scheme.`,
        why: "Employer match is free money. Every £1 you sacrifice into pension attracts £1 from the employer AND tax + NI relief. No other option beats this on 1-year return.",
        weight: 200,
      });
    }

    // --- LISA ---
    if (lisaEligible) {
      const usable = Math.min(gift, taxYear.isaLisa.lisaAnnualLimit);
      const b = lisaBonusFor(usable, taxYear.isaLisa);
      opts.push({
        key: "lisa",
        label: "Lifetime ISA (LISA)",
        badge: "25% bonus",
        badgeTone: "success",
        immediateValue: b.totalIntoLISA,
        immediateMultiple: b.returnMultiple,
        horizonValue: b.totalIntoLISA * growthFactor,
        access:
          firstHome === "yes"
            ? "For your first home (under £450k) or from age 60"
            : firstHome === "maybe"
              ? "For a first home under £450k or from age 60; else 25% penalty on withdrawal"
              : "From age 60 (or 25% early-withdrawal penalty)",
        constraints: `£${taxYear.isaLisa.lisaAnnualLimit.toLocaleString()}/yr contribution cap. Uses part of your £${taxYear.isaLisa.isaAnnualLimit.toLocaleString()} ISA allowance. Must have held the LISA 12 months before first-home withdrawal.`,
        why: firstHome === "yes"
          ? "You're saving for a first home and are LISA-age. This is a 25% instant top-up on up to £4,000/yr — the highest safe return available. Do this to the cap first."
          : firstHome === "maybe"
            ? "The 25% bonus is the highest safe return going. Locking your money to first-home-or-age-60 is real, but the 25% out-performs any other option even after tax on early-withdrawal in most scenarios."
            : "Even without first-home plans, the 25% bonus plus tax-free growth to age 60 is strong for long-horizon pension-adjacent saving. Weigh against pension top-up given lockup similarity.",
        weight: firstHome === "yes" ? 150 : firstHome === "maybe" ? 130 : 90,
      });
    }

    // --- JISA for a child ---
    if (isChild && graduate.currentAge < 18) {
      const jisaCap = taxYear.isaLisa.jisaAnnualLimit;
      const usable = Math.min(gift, jisaCap);
      opts.push({
        key: "jisa",
        label: "Junior ISA (JISA)",
        badge: "tax-free growth",
        badgeTone: "success",
        immediateValue: usable,
        immediateMultiple: 1.0,
        horizonValue: usable * growthFactor,
        access: "Locked until child's 18th birthday, then becomes their adult ISA",
        constraints: `£${jisaCap.toLocaleString()}/yr cap. Contributor cannot access; the child owns the money.`,
        why: "Tax-free growth to 18. If the horizon is 10+ years, S&S JISA usually beats Cash JISA. Unlike a bare trust, contributions can't be recovered by the parent.",
        weight: 120,
      });
    }

    // --- Pension top-up beyond match ---
    if (!isChild) {
      const netCost = netCostOfContribution(
        gift, marginal.tax, marginal.ni, "salarySacrifice",
      );
      const pctGain = netCost > 0 ? gift / netCost : 1.0;
      opts.push({
        key: "pension",
        label: "Pension top-up (beyond employer match)",
        badge:
          marginal.combined > 0.5 ? "62% marginal zone" :
            marginal.tax >= 0.40 ? "40% relief" : "20% relief",
        badgeTone: marginal.combined > 0.5 ? "warning" : "success",
        immediateValue: gift,
        immediateMultiple: pctGain,
        horizonValue: gift * growthFactor,
        access: "Locked until age 55–58",
        constraints: `£60k annual allowance (tapered above £260k adjusted income). Salary-sacrifice route assumed.`,
        why: marginal.combined > 0.5
          ? "You're in the £100–125k 62% marginal zone. Salary sacrifice into pension is extraordinarily valuable here — the £1 sacrificed costs ~38p in take-home."
          : marginal.tax >= 0.40
            ? "Higher-rate salary sacrifice keeps 42% (tax + NI) that would otherwise leave your take-home. Long lockup, but the immediate uplift is real."
            : "Basic-rate salary sacrifice keeps 28% (20% tax + 8% NI). Not as spectacular as the LISA bonus at your age but far higher annual cap.",
        weight: marginal.combined > 0.5 ? 160 : marginal.tax >= 0.40 ? 110 : 80,
      });
    }

    // --- Stocks & Shares ISA ---
    if (!isChild) {
      opts.push({
        key: "ss-isa",
        label: "Stocks & Shares ISA",
        badge: "tax-free growth",
        badgeTone: "neutral",
        immediateValue: gift,
        immediateMultiple: 1.0,
        horizonValue: gift * growthFactor,
        access: "Any time",
        constraints: `£${taxYear.isaLisa.isaAnnualLimit.toLocaleString()}/yr allowance across all your ISAs (Cash + S&S + LISA + IF).`,
        why: "No immediate uplift, but tax-free growth and fully flexible access. The default answer for money that doesn't have a specific home elsewhere.",
        weight: 60,
      });
    }

    // --- Student loan overpayment ---
    if (!isChild && graduate.loanBalance > 0) {
      const rate = annualInterestRate(graduate.salary, planData, {
        rpiMarch,
        bankRate: taxYear.assumedIndices.bankRateDefault,
      });
      const oneYearSavedInterest = gift * rate;
      // Rough "will this ever wipe" heuristic: if plan wipes at 40 yrs and
      // salary is near threshold, overpay is likely wasted.
      const willLikelyWipe =
        planData.wipeAfterYears >= 30 &&
        annualRepayment(graduate.salary, planData) < gift * rate;
      opts.push({
        key: "loan",
        label: "Overpay student loan",
        badge: willLikelyWipe ? "likely gifted to Treasury" : "guaranteed interest saving",
        badgeTone: willLikelyWipe ? "danger" : "neutral",
        immediateValue: gift + oneYearSavedInterest,
        immediateMultiple: 1 + rate,
        horizonValue: gift * Math.pow(1 + rate, Math.min(yearsGrow, 5)),
        access: "One-way — cannot get it back",
        constraints: `Loan interest rate ${(rate * 100).toFixed(2)}%. Overpayments are non-reversible.`,
        why: willLikelyWipe
          ? "At your current salary the loan will likely wipe after " +
            planData.wipeAfterYears +
            " years. Overpaying gifts money to Treasury without shortening the term — model your loan on the Loan modeller tab to confirm."
          : "Only makes sense if you'll clear the loan in full before the wipe date. Compare the interest rate above against a mortgage rate or pension return — usually pension wins.",
        weight: willLikelyWipe ? -50 : 30,
      });
    }

    // Sort by weight descending
    opts.sort((a, b) => b.weight - a.weight);
    // Pin "Overpay student loan" to the top of the list — most graduates'
    // instinctive first thought. Keeps the option front-and-centre so users
    // can quickly see what it would (or wouldn't) do to their loan.
    const loanIdx = opts.findIndex((o) => o.key === "loan");
    if (loanIdx > 0) {
      const [loan] = opts.splice(loanIdx, 1);
      opts.unshift(loan);
    }
    return opts;
  });

  // The recommendation banner picks the option with the highest MERIT
  // (weight), which may not be the pinned first card. This is deliberate:
  // "your first thought is loan overpay; the actual best option is X".
  const bestOption = $derived(
    [...options].sort((a, b) => b.weight - a.weight)[0],
  );

  function applyGiftToLoan(gift) {
    graduate.loanBalance = Math.max(0, graduate.loanBalance - gift);
  }

  const fmtGBP = (n) =>
    new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      maximumFractionDigits: 0,
    }).format(n);
  const fmtPct = (n) =>
    new Intl.NumberFormat("en-GB", {
      style: "percent",
      maximumFractionDigits: 1,
    }).format(n);
</script>

<section class="screen">
  <div class="intro">
    <h2>Gift decision tool</h2>
    <p>
      Someone's given you a lump sum. What's the highest-return home for it,
      given your age, plans, and current setup? Options ranked by effective
      return under your inputs — not personal advice.
    </p>
  </div>

  <div class="layout">
    <aside class="inputs card">
      <h3>The gift</h3>

      <label class="field">
        <span class="label">Amount</span>
        <div class="prefix-input">
          <span class="prefix">£</span>
          <input type="number" min="0" step="100" bind:value={giftAmount} />
        </div>
      </label>

      <label class="field">
        <span class="label">Life stage</span>
        <select bind:value={lifeStage}>
          {#each LIFE_STAGES as ls (ls.key)}
            <option value={ls.key}>{ls.label}</option>
          {/each}
        </select>
      </label>

      <label class="field">
        <span class="label">Age</span>
        <input type="number" min="0" max="80" step="1" bind:value={graduate.currentAge} />
        <span class="hint-sync">Shared across tabs</span>
      </label>

      <h3 class="section-heading">Your setup</h3>

      <label class="field">
        <span class="label">Emergency fund</span>
        <select bind:value={emergencyState}>
          {#each EMERGENCY_STATES as e (e.key)}
            <option value={e.key}>{e.label}</option>
          {/each}
        </select>
      </label>

      {#if !isChild}
        <label class="field">
          <span class="label">Buying a first home?</span>
          <select bind:value={firstHome}>
            {#each HOUSING_INTENTS as h (h.key)}
              <option value={h.key}>{h.label}</option>
            {/each}
          </select>
        </label>

        <label class="field checkbox-field">
          <input type="checkbox" bind:checked={hasPensionMatch} />
          <span>Employer offers pension match</span>
        </label>

        {#if hasPensionMatch}
          <label class="field">
            <span class="label">Match room remaining</span>
            <div class="suffix-input">
              <input type="number" min="0" max="20" step="0.5" bind:value={matchPctRemaining} />
              <span class="suffix">% of salary</span>
            </div>
            <span class="hint">0 if already maxed on the Pension tab</span>
          </label>
        {/if}
      {/if}

      <h3 class="section-heading">Assumptions</h3>

      <label class="field">
        <span class="label">Long-term real return</span>
        <div class="suffix-input">
          <input type="number" min="-5" max="15" step="0.1" bind:value={realReturn} />
          <span class="suffix">%</span>
        </div>
      </label>

      <label class="field">
        <span class="label">Horizon</span>
        <div class="suffix-input">
          <input type="number" min="1" max="60" step="1" bind:value={horizonYears} />
          <span class="suffix">years</span>
        </div>
      </label>
    </aside>

    <section class="output">
      <!-- Recommendation banner -->
      {#if bestOption}
        <div class="steer card">
          <div class="steer-label">Highest-return option under your inputs</div>
          <div class="steer-title">{bestOption.label}</div>
          <div class="steer-why">{bestOption.why}</div>
          {#if bestOption.key !== "loan" && options[0]?.key === "loan"}
            <div class="steer-note">
              Note: paying off the student loan is shown first below because
              it's most people's instinctive first thought — but the maths
              points to the option above.
            </div>
          {/if}
        </div>
      {/if}

      <!-- Options list — loan overpay pinned first, then merit order -->
      <div class="options">
        {#each options as opt, i (opt.key)}
          <div class="option-card card" class:option-top={i === 0}>
            <div class="option-head">
              <div class="option-rank">{i + 1}</div>
              <div class="option-title-block">
                <div class="option-title">
                  {opt.label}
                  <span class="tag tag-{opt.badgeTone}">{opt.badge}</span>
                </div>
                <div class="option-access">Access: {opt.access}</div>
              </div>
            </div>

            <div class="option-stats">
              <div class="stat">
                <span class="stat-label">£1 in →</span>
                <span class="stat-value">
                  £{opt.immediateMultiple === Infinity ? "∞" : opt.immediateMultiple.toFixed(2)}
                </span>
                <span class="stat-hint">immediate value</span>
              </div>
              <div class="stat">
                <span class="stat-label">Deposit → today</span>
                <span class="stat-value">{fmtGBP(opt.immediateValue)}</span>
                <span class="stat-hint">including bonus/relief</span>
              </div>
              <div class="stat">
                <span class="stat-label">In {horizonYears}yr @ {fmtPct(realReturn / 100)}</span>
                <span class="stat-value">{fmtGBP(opt.horizonValue)}</span>
                <span class="stat-hint">real, today's money</span>
              </div>
            </div>

            <div class="option-constraints">
              <strong>Constraints:</strong> {opt.constraints}
            </div>
            <div class="option-why">
              {opt.why}
            </div>

            {#if opt.key === "loan"}
              <div class="apply-block">
                <button
                  type="button"
                  class="apply-btn"
                  disabled={giftAmount <= 0 || graduate.loanBalance <= 0}
                  onclick={() => applyGiftToLoan(giftAmount)}
                >
                  Apply {fmtGBP(giftAmount)} to student loan →
                </button>
                <div class="apply-hint">
                  Current balance {fmtGBP(graduate.loanBalance)}
                  → would be {fmtGBP(Math.max(0, graduate.loanBalance - giftAmount))}.
                  Loan modeller chart updates immediately.
                </div>
              </div>
            {/if}
          </div>
        {/each}
      </div>

      {#if !isChild && lifeStage === "grad" && graduate.salary >= 100000 && graduate.salary <= 125140}
        <div class="callout card">
          <strong>The £100–125k marginal-rate trap:</strong> at your salary the
          next £1 you earn is taxed effectively at 62%. If any of this gift
          could be redirected into your own pension via salary sacrifice
          this tax year, you'd shield income you'd otherwise lose to the
          personal-allowance taper. Consult a regulated adviser before
          restructuring compensation.
        </div>
      {/if}

      <div class="footnote card">
        <strong>How the ranking works.</strong> Each option shows: immediate
        uplift (bonus, tax relief, employer match), effective return per £1,
        and value at your chosen horizon under the real-return assumption.
        The recommendation banner picks the option with the strongest
        combination for your current setup. This is a mechanical comparison
        under your own inputs — not a personal recommendation to buy any
        product. Consider an FCA-regulated adviser for choices that will
        materially affect your finances.
      </div>
    </section>
  </div>
</section>

<style>
  .screen { display: flex; flex-direction: column; gap: 1.25rem; }
  .intro h2 { margin-bottom: 0.35rem; }
  .intro p { color: var(--fg-muted); margin: 0; font-size: 0.95rem; max-width: 68ch; }

  .layout { display: grid; grid-template-columns: 1fr; gap: 1.25rem; }
  @media (min-width: 900px) {
    .layout { grid-template-columns: minmax(300px, 340px) 1fr; }
  }

  .card {
    background: var(--card);
    border: 1px solid var(--card-border);
    border-radius: 0.9rem;
    padding: 1.15rem 1.25rem;
    box-shadow: var(--card-shadow);
  }

  .inputs h3 { margin-bottom: 0.9rem; }
  .section-heading {
    margin-top: 1.25rem;
    padding-top: 1rem;
    border-top: 1px solid var(--card-border);
    margin-bottom: 0.9rem;
  }
  .field { display: flex; flex-direction: column; gap: 0.35rem; margin-bottom: 0.85rem; }
  .checkbox-field {
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.85rem;
  }
  .checkbox-field input { width: auto; }
  .checkbox-field span { font-size: 0.9rem; color: var(--fg); }
  .label {
    font-size: 0.8rem; font-weight: 600; color: var(--fg-muted);
    text-transform: uppercase; letter-spacing: 0.04em;
  }
  input, select {
    font: inherit; font-size: 1rem; padding: 0.55rem 0.75rem;
    border: 1px solid var(--card-border); border-radius: 0.5rem;
    background: var(--bg); color: var(--fg); width: 100%;
    transition: border-color 0.15s, background 0.15s;
  }
  input:focus, select:focus { outline: none; border-color: var(--accent); background: var(--card); }
  .prefix-input, .suffix-input { position: relative; }
  .prefix-input input { padding-left: 1.75rem; }
  .prefix { position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%); color: var(--fg-muted); font-weight: 600; pointer-events: none; }
  .suffix-input input { padding-right: 5.5rem; }
  .suffix { position: absolute; right: 0.75rem; top: 50%; transform: translateY(-50%); color: var(--fg-muted); font-weight: 600; pointer-events: none; font-size: 0.8rem; }
  .hint { color: var(--fg-subtle); font-size: 0.78rem; }
  .hint-sync {
    color: var(--accent);
    font-size: 0.7rem;
    font-weight: 500;
    opacity: 0.75;
    font-style: italic;
  }

  .output { display: flex; flex-direction: column; gap: 1.25rem; }

  /* Steer banner */
  .steer {
    background: linear-gradient(135deg, var(--accent) 0%, var(--accent-2) 100%);
    color: var(--accent-fg);
    border: none;
    padding: 1.5rem;
  }
  .steer-label {
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    opacity: 0.85;
    font-weight: 700;
  }
  .steer-title {
    font-size: 1.7rem;
    font-weight: 800;
    margin: 0.35rem 0 0.55rem;
    line-height: 1.15;
  }
  .steer-why {
    font-size: 0.95rem;
    line-height: 1.55;
    opacity: 0.95;
    max-width: 70ch;
  }
  .steer-note {
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px dashed rgba(255, 255, 255, 0.25);
    font-size: 0.85rem;
    opacity: 0.85;
    line-height: 1.5;
  }

  /* Options list */
  .options { display: flex; flex-direction: column; gap: 0.9rem; }
  .option-card {
    padding: 1.15rem 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
    transition: border-color 0.15s;
  }
  .option-card.option-top {
    border-color: var(--accent);
    border-width: 2px;
    padding: calc(1.15rem - 1px) calc(1.25rem - 1px);
  }

  .option-head {
    display: flex;
    gap: 0.9rem;
    align-items: flex-start;
  }
  .option-rank {
    width: 30px; height: 30px;
    background: var(--bg-2);
    color: var(--fg-muted);
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
    font-size: 0.9rem;
    flex-shrink: 0;
  }
  .option-top .option-rank {
    background: var(--accent);
    color: var(--accent-fg);
  }
  .option-title-block { min-width: 0; flex: 1; }
  .option-title {
    font-weight: 800;
    color: var(--fg);
    font-size: 1.05rem;
    line-height: 1.3;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
  .option-access {
    font-size: 0.82rem;
    color: var(--fg-muted);
    margin-top: 0.2rem;
  }

  .tag {
    font-size: 0.7rem;
    padding: 0.15em 0.55em;
    border-radius: 0.35em;
    font-weight: 700;
    letter-spacing: 0.02em;
    display: inline-block;
  }
  .tag-success { background: rgba(30, 126, 94, 0.15); color: var(--success); }
  .tag-warning { background: rgba(207, 138, 44, 0.15); color: var(--warning); }
  .tag-danger { background: rgba(184, 63, 58, 0.15); color: var(--danger); }
  .tag-neutral { background: var(--bg-2); color: var(--fg-muted); }

  .option-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.6rem;
  }
  .stat {
    background: var(--bg-2);
    border-radius: 0.5rem;
    padding: 0.6rem 0.7rem;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }
  .stat-label {
    font-size: 0.68rem;
    color: var(--fg-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
  }
  .stat-value {
    font-size: 1.1rem;
    font-weight: 800;
    color: var(--fg);
    font-variant-numeric: tabular-nums;
  }
  .stat-hint {
    font-size: 0.7rem;
    color: var(--fg-subtle);
  }

  .option-constraints, .option-why {
    font-size: 0.88rem;
    line-height: 1.5;
    color: var(--fg-muted);
  }
  .option-constraints strong, .option-why strong { color: var(--fg); }
  .option-why { color: var(--fg); }

  .apply-block {
    margin-top: 0.35rem;
    padding-top: 0.85rem;
    border-top: 1px dashed var(--card-border);
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }
  .apply-btn {
    align-self: flex-start;
    background: linear-gradient(135deg, var(--accent), var(--accent-2));
    color: var(--accent-fg);
    border: none;
    padding: 0.6rem 1rem;
    border-radius: 0.5rem;
    font-weight: 700;
    font-size: 0.9rem;
    transition: transform 0.1s, box-shadow 0.15s;
    box-shadow: 0 3px 8px rgba(15, 61, 62, 0.2);
  }
  .apply-btn:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 5px 12px rgba(15, 61, 62, 0.28);
  }
  .apply-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  .apply-hint {
    font-size: 0.82rem;
    color: var(--fg-muted);
    line-height: 1.5;
  }

  .callout {
    background: rgba(207, 138, 44, 0.08);
    border-color: rgba(207, 138, 44, 0.3);
    color: var(--fg);
    font-size: 0.92rem;
    line-height: 1.55;
  }

  .footnote {
    background: var(--bg-2);
    border: none;
    box-shadow: none;
    color: var(--fg-muted);
    font-size: 0.82rem;
    line-height: 1.55;
  }
  .footnote strong { color: var(--fg); }

  @media (max-width: 500px) {
    .option-stats { grid-template-columns: 1fr; }
    .steer-title { font-size: 1.3rem; }
  }
</style>
